import { formatInTimeZone } from "date-fns-tz";
import {
  CalendarView,
  Icons,
  ResourceSlot,
} from "@pushpress/shared-components";
import { joinStrings } from "../../../utils/misc";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import {
  AppointmentTypeFragment,
  StaffBusyTimesFragment,
  StaffFragment,
} from "./AppointmentsStart-generated.hooks";
import {
  AppointmentStatusName,
  getAppointmentStatusName,
} from "../AppointmentsUtils";
import { AppointmentFilter } from "../Appointments.types";
import { FunctionComponent } from "react";
import { AppointmentEvent } from "../../../apis/appointmentApi";

export const appointmentCalendarViews = [
  "day",
  "week",
  "month",
  "list",
  "singleStaff",
  "multiStaff",
] as const;

export type AppointmentCalendarView = (typeof appointmentCalendarViews)[number];

export type View = "day" | "week" | "month" | "list" | "resource";

export interface ViewOption {
  value: AppointmentCalendarView;
  view: View;
  label: string;
  icon: FunctionComponent;
}

export interface ListViewOption {
  label: string;
  value: CalendarView;
}

export function filterAppointmentsItems(
  items: AppointmentEvent[],
  filter?: AppointmentFilter,
): AppointmentEvent[] {
  // not show if it is appointment and it is not checked
  return items.filter((item) => {
    // not show if it doesn't have the member
    if (filter?.member && filter?.member.userUuid !== item.user.uuid) {
      return false;
    }

    // not show if it doesn't have the appointment type
    if (
      filter?.appointmentTypes?.length &&
      !filter?.appointmentTypes.some(
        (at) => at.uuid === item.appointmentType?.uuid,
      )
    ) {
      return false;
    }

    // not show if it doesn't have the staff
    if (
      filter?.staff?.length &&
      !filter?.staff.some((s) => s.userUuid === item.staff?.uuid)
    ) {
      return false;
    }

    // not show by status
    if (filter?.status?.length) {
      return filter.status.some(
        (option) => getAppointmentStatusName(item) === option.value,
      );
    }

    return true;
  });
}

export function getAppointmentsFilterTags(
  filter?: AppointmentFilter,
  ignoreStaff?: boolean,
): { label: string; onClose: () => AppointmentFilter }[] {
  const result = [];

  // tags by member
  if (filter?.member) {
    const member = filter.member;
    const label = joinStrings(member.firstName, member.lastName);
    result.push({
      label,
      onClose: () => ({
        ...filter,
        member: undefined,
      }),
    });
  }

  // tags by appointments types
  const appointmentTypes = filter?.appointmentTypes || [];
  appointmentTypes.forEach((at) => {
    const label = at.name;
    result.push({
      label,
      onClose: () => ({
        ...filter,
        appointmentTypes: filter?.appointmentTypes.filter(
          (item) => item.uuid !== at.uuid,
        ),
      }),
    });
  });

  // tags by staff
  if (!ignoreStaff) {
    const staff = filter?.staff || [];
    staff.forEach((s) => {
      const label = joinStrings(s.firstName, s.lastName);
      result.push({
        label,
        onClose: () => ({
          ...filter,
          staff: filter?.staff.filter((item) => item.userUuid !== s.userUuid),
        }),
      });
    });
  }

  // tags by status
  const status = filter?.status || [];
  status.forEach((s) => {
    const label = s.label;
    result.push({
      label,
      onClose: () => ({
        ...filter,
        status: filter?.status.filter((item) => item.label !== s.label),
      }),
    });
  });

  return result;
}

const ISO_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss'Z'";

export const convertToTimezone = (date: Date, timezone: string) => {
  const formatted = formatInTimeZone(date, timezone, ISO_DATE_FORMAT);
  return new Date(formatted);
};

export function filterAvailableStaff(
  staff: StaffFragment[],
  appointmentTypes: AppointmentTypeFragment[],
) {
  if (!appointmentTypes?.length || !staff?.length) {
    return [];
  }

  const AppointmentStaffs = appointmentTypes
    .map((appointmentType) => appointmentType.staff)
    .flat();

  const staffWithAppointment = staff?.filter((entry) => {
    return AppointmentStaffs.some((item) => item?.userUuid === entry.userUuid);
  });

  if (staffWithAppointment?.length > 0) {
    return staffWithAppointment;
  }

  return staff;
}

export function getViewRange(
  date: Date,
  view: AppointmentCalendarView,
  listView: CalendarView,
) {
  let startDate = startOfDay(date);
  let endDate = endOfDay(date);
  if (
    view === "week" ||
    view === "singleStaff" ||
    (view === "list" && listView === "listWeek")
  ) {
    startDate = startOfWeek(date);
    endDate = endOfWeek(date);
  } else if (
    view === "month" ||
    (view === "list" && listView === "listMonth")
  ) {
    startDate = startOfMonth(date);
    endDate = endOfMonth(date);
  }
  return {
    startDate,
    endDate,
  };
}

export function convertToResourceSlot(
  staff?: StaffBusyTimesFragment[],
  timezone?: string,
): ResourceSlot[] {
  if (!staff) {
    return [];
  }
  const result: ResourceSlot[] = [];
  staff.forEach((user) => {
    if (user.busyTimes) {
      user.busyTimes.forEach((time) => {
        result.push({
          id: `id-${result.length + 1}`,
          resourceId: user.userUuid,
          start: convertToTimezone(new Date(time.startTime), timezone),
          end: convertToTimezone(new Date(time.endTime), timezone),
        });
      });
    }
  });
  return result;
}

export interface StatusFilterOption {
  label: string;
  value: AppointmentStatusName;
}

export const statusOptions: StatusFilterOption[] = [
  { label: "Booked (unpaid)", value: "BOOKED (UNPAID)" },
  { label: "Booked", value: "BOOKED" },
  { label: "Cancelled", value: "CANCELLED" },
  { label: "Checked in", value: "CHECKED IN" },
  { label: "Forgiven", value: "FORGIVEN" },
  { label: "No show", value: "NO SHOW" },
  { label: "Unpaid no show", value: "UNPAID NO SHOW" },
  { label: "Unpaid late cancel", value: "UNPAID LATE CANCEL" },
  { label: "Late cancel", value: "LATE CANCEL" },
];

export const listViewOptions: ListViewOption[] = [
  { label: "Day", value: "listDay" },
  { label: "Week", value: "listWeek" },
  { label: "Month", value: "listMonth" },
];

export const viewOptions: ViewOption[] = [
  { value: "day", view: "day", label: "Day", icon: Icons.CalendarDay },
  {
    value: "week",
    view: "week",
    label: "Week",
    icon: Icons.CalendarWeek,
  },
  {
    value: "month",
    view: "month",
    label: "Month",
    icon: Icons.CalendarMonth,
  },
  { value: "list", view: "list", label: "List", icon: Icons.Toc },
  {
    value: "singleStaff",
    view: "week",
    label: "Single staff availability",
    icon: Icons.Person,
  },
  {
    value: "multiStaff",
    view: "resource",
    label: "Multi staff availability",
    icon: Icons.Group,
  },
];
